import { StandardEvents } from '@shopify/events';

/**
 * Freshness Finder.
 *
 * A three-step quiz that recommends one of three tiers. Household size picks the
 * tier; the products behind each tier are assigned in the theme customizer, so
 * no product handles or variant IDs are hardcoded here.
 */

const TAG = 'freshness-finder';
const TOTAL_STEPS = 3;

class FreshnessFinder extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;

    const config = this.querySelector('[data-tiers]');
    if (!config) return;

    try {
      this.tiers = JSON.parse(config.textContent);
    } catch {
      return;
    }

    this.answers = {};
    this.step = 1;
    this.ready = true;

    this.addEventListener('click', this.handleClick);
    this.update();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
  }

  handleClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const option = target.closest('[data-answer]');
    if (option) return this.choose(option);

    if (target.closest('[data-restart]')) return this.restart();
    if (target.closest('[data-add-to-cart]')) return this.addToCart();
  };

  choose(button) {
    const key = button.dataset.answer;
    this.answers[key] = button.dataset.value;
    if (button.dataset.tier) this.answers.tier = button.dataset.tier;

    for (const sibling of button.parentElement?.children ?? []) {
      sibling.setAttribute('aria-pressed', String(sibling === button));
    }

    this.step = this.step >= TOTAL_STEPS ? 'result' : this.step + 1;
    this.update();
  }

  restart() {
    this.answers = {};
    this.step = 1;
    for (const card of this.querySelectorAll('[data-answer]')) card.setAttribute('aria-pressed', 'false');
    const status = this.querySelector('[data-status]');
    if (status) status.textContent = '';
    const cta = this.querySelector('[data-add-to-cart]');
    if (cta) cta.removeAttribute('disabled');
    this.update();
  }

  update() {
    const showing = String(this.step);
    for (const panel of this.querySelectorAll('[data-step]')) {
      panel.classList.toggle('is-active', panel.dataset.step === showing);
    }

    const progress = this.querySelector('[data-progress]');
    const fill = this.querySelector('[data-fill]');
    const count = this.querySelector('[data-count]');
    const onResult = this.step === 'result';

    if (progress) progress.toggleAttribute('hidden', onResult);
    if (!onResult) {
      if (fill) fill.style.width = `${(this.step / TOTAL_STEPS) * 100}%`;
      if (count) count.textContent = `Step ${this.step} of ${TOTAL_STEPS}`;
    }

    if (onResult) this.renderResult();
  }

  get tier() {
    return this.tiers?.[this.answers.tier] ?? null;
  }

  renderResult() {
    const tier = this.tier;
    const name = this.querySelector('[data-result-name]');
    const reason = this.querySelector('[data-result-reason]');
    const cta = this.querySelector('[data-add-to-cart]');
    const status = this.querySelector('[data-status]');

    if (name) name.textContent = tier?.name ?? '';

    if (reason) {
      reason.textContent = (this.tiers.reasonTemplate || '')
        .replace('{size}', this.answers.size ?? '')
        .replace('{waste}', (this.answers.waste ?? '').toLowerCase())
        .replace('{storage}', (this.answers.storage ?? '').toLowerCase());
    }

    // With no product assigned yet there is nothing to add, so say so plainly
    // rather than firing a cart call that would fail.
    const sellable = Boolean(tier?.assigned && tier?.variantId);
    if (cta) cta.toggleAttribute('disabled', !sellable);
    if (status) status.textContent = sellable ? '' : this.tiers.unassignedMessage || '';
  }

  async addToCart() {
    const tier = this.tier;
    const cta = this.querySelector('[data-add-to-cart]');
    const status = this.querySelector('[data-status]');
    if (!tier?.variantId || !cta) return;

    cta.setAttribute('disabled', '');
    if (status) status.textContent = 'Adding…';

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: tier.variantId, quantity: 1 }] }),
      });
      if (!response.ok) throw new Error(`cart/add returned ${response.status}`);

      if (status) status.textContent = 'Added to your cart.';
      await this.notifyCart();
    } catch (error) {
      console.warn('[freshness-finder] Add to cart failed:', error);
      if (status) status.textContent = 'Something went wrong. Please try again.';
    } finally {
      cta.removeAttribute('disabled');
    }
  }

  /** Lets the header cart bubble and drawer pick up the new line. */
  async notifyCart() {
    try {
      const cart = await (await fetch('/cart.js')).json();
      const event = new Event(StandardEvents.cartLinesUpdate, { bubbles: true });
      event.promise = Promise.resolve({
        cart: { totalQuantity: cart.item_count },
        detail: { itemCount: cart.item_count },
      });
      document.dispatchEvent(event);
    } catch (error) {
      console.warn('[freshness-finder] Cart refresh failed:', error);
    }
  }
}

if (!customElements.get(TAG)) customElements.define(TAG, FreshnessFinder);
